/**
 * @flow
 */

import { navigate } from "gatsby"
import _ from "lodash"
import React, { useCallback, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import styled from "styled-components"

import Auth from "@aws-amplify/auth"
import { useQuery } from "@apollo/react-hooks"

import { PRACTICE_QUERY } from "../queries"
import { notify, setUser } from "../state"
import { titleLabel } from "../utils/titles"
import CreditCardForm from "./CreditCardForm"
import PlanForm from "./PlanForm"
import PracticeForm from "./PracticeForm.js"
import FormContainer from "./elements/FormContainer"
import Modal from "./elements/Modal"
import PageLoader from "./elements/PageLoader"
import StepIndicator from "./elements/StepIndicator"
import Layout from "./layout"
import { SEO } from "./utils"
import Button from "./elements/Button"

const EMPTY_PRACTICE = {
  name: "",
  practiceNumber: "",
  telephoneNumber: "",
  physicalAddress: "",
  specialty: "",
}

const SignOutBar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
`

const LogoutButton = styled(Button)`
  padding: 0 3rem;
  background: rgb(255, 179, 161, 1);
  background: linear-gradient(
    90deg,
    rgba(255, 179, 161, 1) 35%,
    rgba(255, 141, 157, 1) 100%
  );
`

const getPracticeName = data => _.get(data, "name")
const getCreditCards = data => _.get(data, "billing.cards", [])
const getActiveSubscriptionQuantity = data =>
  _.get(data, "billing.activeSubscriptionQuantity", 0)
const getPlanCost = data => _.get(data, "billing.planCost", 0)
const getCurrentUserCount = data => {
  return (
    _.get(data, "billing.currentUserCount", 0) +
    _.get(data, "billing.currentInviteCount", 0)
  )
}

const GetStartedScreen = () => {
  const [activeStep, setActiveStep] = useState(0)
  const [practice, setPractice] = useState(null)
  const [practiceInitial, setPracticeInitial] = useState(EMPTY_PRACTICE)
  const [showPrefillModal, setShowPrefillModal] = useState(false)
  const [prefillPrompted, setPrefillPrompted] = useState(false)
  const dispatch = useDispatch()
  const user = useSelector(state => state.auth.user)

  const handleSignOut = async () => {
    await Auth.signOut()
    dispatch(setUser())
  }

  const error = useCallback(
    message => {
      dispatch(
        notify({
          message: message,
          variant: "error",
        })
      )
    },
    [dispatch]
  )

  const success = useCallback(
    message => {
      dispatch(
        notify({
          message: message,
          variant: "success",
        })
      )
    },
    [dispatch]
  )

  const result = useQuery(PRACTICE_QUERY, {
    notifyOnNetworkStatusChange: true,
  })

  const fetching = result.networkStatus < 7

  useEffect(() => {
    if (!fetching && result.data) {
      const { practice } = result.data.me
      setPractice(practice)

      const name = getPracticeName(practice)
      const creditCards = getCreditCards(practice)
      const activeSubscriptionQuantity = getActiveSubscriptionQuantity(practice)

      if (_.isEmpty(name)) {
        setActiveStep(0)
        if (!prefillPrompted) {
          setShowPrefillModal(true)
        }
      } else if (_.isEmpty(creditCards)) {
        setActiveStep(1)
      } else if (activeSubscriptionQuantity === 0) {
        setActiveStep(2)
      } else {
        navigate("/app")
      }
    }
  }, [fetching, result.data, prefillPrompted])

  const handleError = (err: Error) => {
    error(err.message)
  }

  const handlePrefillConfirm = () => {
    const name = `${titleLabel(user && user.title)} ${(user &&
      user.firstName) ||
      ""} ${(user && user.lastName) || ""}`.trim()
    setPracticeInitial({
      ...EMPTY_PRACTICE,
      name,
      telephoneNumber: (user && user.mobileNumber) || "",
    })
    setPrefillPrompted(true)
    setShowPrefillModal(false)
  }

  const handlePrefillCancel = () => {
    setPrefillPrompted(true)
    setShowPrefillModal(false)
  }

  // eslint-disable-next-line no-unused-vars
  const handlePracticeSave = data => {
    success("Practice saved successfully.")
    result.refetch()
  }

  const handleCreditCardSave = () => {
    success("Credit Card loaded successfully.")
    result.refetch()
  }

  const handlePlanChange = ({ quantity }) => {
    if (quantity > 1) {
      success("Subscription activated successfully.")
    } else {
      success("Plan updated successfully.")
    }
    result.refetch()
  }

  const renderStep = () => {
    if (activeStep === 0) {
      return (
        <div>
          <h2>Create a new practice</h2>
          <h4>Tell us about your practice?</h4>
          <PracticeForm
            data={practiceInitial}
            requireDirty={false}
            submitButtonTitle="Next"
            onSave={handlePracticeSave}
            onError={handleError}
          />
        </div>
      )
    } else if (activeStep === 1) {
      return (
        <div>
          <h2>Load a Credit Card</h2>
          <h4>
            Enter the Credit Card details to use for the monthly subscription.
          </h4>
          <CreditCardForm
            onSuccess={handleCreditCardSave}
            onError={handleError}
            invertBackground
          />
        </div>
      )
    } else if (activeStep === 2) {
      const activeSubscriptionQuantity = getActiveSubscriptionQuantity(practice)
      const currentUserCount = getCurrentUserCount(practice)
      const planCost = getPlanCost(practice)
      return (
        <div>
          <h2>Activate your subscription.</h2>
          <h4>Start your plan with the desired number of users.</h4>
          <PlanForm
            activeSubscriptionQuantity={activeSubscriptionQuantity}
            currentUserCount={currentUserCount}
            planCost={planCost}
            onChange={handlePlanChange}
            onError={handleError}
            invertBackground
          />
        </div>
      )
    }
    return null
  }

  return (
    <Layout metaNavbar={true}>
      <SEO title="Get Started" />
      {fetching ? (
        <PageLoader />
      ) : (
        <FormContainer>
          <StepIndicator step={activeStep} />
          {renderStep()}
          <SignOutBar>
            <LogoutButton onClick={handleSignOut}>Sign out</LogoutButton>
          </SignOutBar>
        </FormContainer>
      )}
      <Modal
        open={showPrefillModal}
        onConfirm={handlePrefillConfirm}
        onCancel={handlePrefillCancel}
        confirmLabel="Yes, use my details"
        cancelLabel="No, thanks"
      >
        Would you like to prefill your practice details with your own
        information?
      </Modal>
    </Layout>
  )
}

export default GetStartedScreen
